import { Mutation } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Toggle } from "react-powerplug";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import RemoveInvitee from "~/lib/mutations/RemoveInvitee";
import {
  IRemoveInviteeMutation,
  IRemoveInviteeMutationVariables,
} from "~/lib/mutations/__generated__/RemoveInvitee.generated";
import Redirect from "~/lib/route/Redirect";
import { IGovernmentUser } from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import InviteModal from "~/components/invite/InviteModal";
import { Margin } from "~/components/spacings";
import Tooltip from "~/components/Tooltip";
import { IGroupProps } from "~/components/group/IGroupProps";

export default function GroupMembersSideBlock({ group }: IGroupProps) {
  const user = useSelfie();
  const intl = useIntl();

  return (
    <Margin mt={24}>
      <div
        data-intercom-target="Group Members Box"
        css={{
          borderRadius: 5,
          border: `1px solid ${palette.lightestGray}`,
          background: "#fff",
        }}
      >
        <div
          css={{
            padding: 16,
            display: "flex",
            borderBottom: `1px solid ${palette.lightestGray}`,
          }}
        >
          <span
            css={{
              fontSize: 12,
              fontWeight: 500,
              color: palette.sealBlue,
              textTransform: "uppercase",
            }}
          >
            <FormattedMessage
              defaultMessage="Members {membersCount}"
              id="Z0dsQh"
              values={{ membersCount: group.membersCount }}
            />
          </span>

          {group.viewerIsMember && (
            <Toggle>
              {({ on: isOpen, toggle }) => (
                <>
                  <InviteModal
                    title={intl.formatMessage({ defaultMessage: "Invite Members", id: "xNdoS4" })}
                    subtitle={
                      <span>
                        <FormattedMessage
                          defaultMessage="They’ll be granted with <strong>Edit</strong> access to this <strong>Group.</strong>"
                          id="RfiLxQ"
                          values={{
                            // eslint-disable-next-line react/display-name
                            strong: (text: string) => <strong>{text}</strong>,
                          }}
                        />
                      </span>
                    }
                    redirectTo={`/groups/${group.slug}`}
                    targetType="GROUP"
                    targetId={group._id}
                    isOpen={isOpen}
                    onRequestClose={toggle}
                    inviteByEmail={false}
                  />

                  <span
                    role="button"
                    css={{
                      marginLeft: "auto",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      color: palette.primary,
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    onClick={toggle}
                  >
                    <FormattedMessage defaultMessage="Invite" id="hYOE+U" />
                  </span>
                </>
              )}
            </Toggle>
          )}
        </div>

        {group.members && group.members.items && (
          <div
            css={{
              padding: 16,
            }}
          >
            <div
              css={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
                gridColumnGap: 12,
                gridRowGap: 12,
              }}
            >
              {(group.membersCount > 6 ? group.members.items.slice(0, 6) : group.members.items).map((member) => {
                if (member.slug) {
                  return (
                    <div
                      css={{
                        position: "relative",
                      }}
                      key={member._id}
                    >
                      {((user as IGovernmentUser)._id === member._id || group.viewerCanRemoveMembers) && (
                        <Mutation<IRemoveInviteeMutation, IRemoveInviteeMutationVariables>
                          mutation={RemoveInvitee}
                          refetchQueries={refetchQueries(["Group"])}
                          variables={{
                            targetId: group._id,
                            targetType: "GROUP",
                            inviteeId: member._id,
                          }}
                        >
                          {(removeInvitee) => (
                            <span
                              role="button"
                              css={{
                                color: palette.white,
                                fontWeight: 600,
                                borderRadius: "100%",
                                position: "absolute",
                                top: -5,
                                right: -5,
                                fontSize: 18,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                width: 20,
                                height: 20,
                                background: palette.red,
                              }}
                              onClick={() => {
                                if (window.confirm("Are you sure?")) {
                                  removeInvitee().then(() => {
                                    if ((user as IGovernmentUser)._id === member._id) {
                                      return <Redirect to="/" />;
                                    }
                                  });
                                }
                              }}
                            >
                              &times;
                            </span>
                          )}
                        </Mutation>
                      )}

                      <Link
                        passHref={true}
                        key={member._id}
                        href="/[userSlug]/[[...tab]]"
                        as={`/@${member.slug}`}
                        legacyBehavior={true}
                      >
                        <a>
                          <Tooltip
                            backgroundColor={palette.innovatorBlue}
                            boxStyle={{
                              padding: 8,
                              boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
                            }}
                            arrowSize={8}
                            render={() => (
                              <span
                                css={{
                                  fontSize: 14,
                                  lineHeight: `${14 * 1.333}px`,
                                  fontWeight: 500,
                                  color: palette.white,
                                }}
                              >
                                {member.fullName || member.firstName}
                              </span>
                            )}
                          >
                            {({ innerRef, bind }: any) => (
                              <ProfilePicture
                                {...bind}
                                innerRef={innerRef}
                                image={member.avatar}
                                name={member.fullName || member.firstName}
                                size="xsmall"
                                css={{
                                  position: "static",
                                  borderRadius: 6,
                                  "& > img": {
                                    borderRadius: 6,
                                    padding: 0,
                                  },
                                  padding: 0,
                                }}
                              />
                            )}
                          </Tooltip>
                        </a>
                      </Link>
                    </div>
                  );
                }

                return (
                  <ProfilePicture
                    key={member._id}
                    image={member.avatar}
                    name={member.firstName}
                    size="xsmall"
                    css={{
                      borderRadius: 6,
                      "& > img": {
                        borderRadius: 6,
                        padding: 0,
                      },
                      padding: 0,
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {group.membersCount > 6 && (
          <Link
            passHref={true}
            href="/groups/[groupSlug]?content=members"
            as={`/groups/${group.slug}?content=members`}
            legacyBehavior={true}
          >
            <a
              css={{
                outline: 0,
                fontSize: 14,
                fontWeight: 500,
                height: 48,
                textAlign: "center",
                textTransform: "uppercase",
                color: palette.sealBlue,
                border: 0,
                display: "block",
                lineHeight: "48px",
                borderTop: `1px solid ${palette.lightestGray}`,
              }}
            >
              <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
            </a>
          </Link>
        )}
      </div>
    </Margin>
  );
}
